/**
 * Tooltip de PARCOURS d'équipe, global : au survol (ou tap) de N'IMPORTE quel
 * drapeau de l'app (groupes, bande du jour, direct, classement Elo, tableau…),
 * on affiche tout le parcours de l'équipe + ses prochains matchs.
 *
 *  - matchs joués : tour · score · adversaire (coloré victoire/défaite) ;
 *  - prochains (flèche →) : matchs de groupe restants si encore en poules,
 *    sinon le prochain match KO ; AUCUN si l'équipe est éliminée ou championne.
 *
 * Un seul écouteur délégué sur `document`. Chaque vue (dashboard, simulation)
 * enregistre son conteneur + un accès à SON état officiel, pour que le parcours
 * affiché corresponde au contexte (réel vs scénario simulé).
 */
import type { OfficialState } from "../app/officialStore.ts";
import { t } from "../app/i18n.ts";
import { esc, flagSpan } from "./html.ts";

function roundShort(id: number): string {
  if (id <= 88) return t("proj.short.r32");
  if (id <= 96) return t("proj.short.r16");
  if (id <= 100) return t("bracket.round.qfShort");
  if (id <= 102) return t("bracket.round.sfShort");
  if (id === 103) return t("sim.thirdPlace");
  return t("proj.round.final");
}

interface Result {
  id: number;
  label: string;
  scoreStr: string;
  opp: string;
  outcome: "w" | "l" | "d";
}
interface Upcoming {
  id: number;
  label: string;
  opp: string | null;
}
type Status = "group" | "ko" | "eliminated" | "champion";
interface Journey {
  played: Result[];
  next: Upcoming[];
  status: Status;
}

function buildJourney(state: OfficialState, team: string): Journey {
  const played: Result[] = [];

  for (const v of state.matches) {
    if (v.match.stage !== "group") continue;
    if (v.match.home !== team && v.match.away !== team) continue;
    const ft = v.displayScore ?? v.live?.score?.ft ?? null;
    if (!ft) continue;
    const isHome = v.match.home === team;
    const gf = ft[isHome ? 0 : 1];
    const ga = ft[isHome ? 1 : 0];
    played.push({
      id: v.match.id,
      label: `${t("thirds.col.group")} ${v.match.group ?? "?"} · ${t("stats.col.played.abbr")}${v.match.matchday ?? "?"}`,
      scoreStr: `${gf}–${ga}`,
      opp: isHome ? v.match.away : v.match.home,
      outcome: gf > ga ? "w" : gf < ga ? "l" : "d",
    });
  }

  let lostKo = false;
  for (const [nid, node] of Object.entries(state.bracket)) {
    if (node.a !== team && node.b !== team) continue;
    const isA = node.a === team;
    const opp = isA ? node.b : node.a;
    const id = Number(nid.slice(1));
    const view = state.byId.get(id);
    const ft = view?.displayScore ?? null;
    if (!ft || opp === null) continue;
    const gf = ft[isA ? 0 : 1];
    const ga = ft[isA ? 1 : 0];
    const pens = view?.penalties;
    const penStr = pens ? ` ${t("common.penaltiesAbbr.v2")} ${pens.score[isA ? 0 : 1]}-${pens.score[isA ? 1 : 0]}` : "";
    const won = node.winner === team;
    if (!won) lostKo = true;
    played.push({
      id,
      label: roundShort(id),
      scoreStr: `${gf}–${ga}${penStr}`,
      opp,
      outcome: gf === ga ? (won ? "w" : "l") : gf > ga ? "w" : "l",
    });
  }
  played.sort((a, b) => a.id - b.id);

  const wonFinal = state.bracket["M104"]?.winner === team;
  const inR32 = Object.values(state.r32Teams).some(([a, b]) => a === team || b === team);
  const eliminated = lostKo || (state.groupsAllComplete && !inR32);
  const status: Status = wonFinal
    ? "champion"
    : eliminated
      ? "eliminated"
      : state.groupsAllComplete
        ? "ko"
        : "group";

  const next: Upcoming[] = [];
  if (status === "group" || status === "ko") {
    // matchs de groupe restants (programmés)
    for (const v of state.matches) {
      if (v.match.stage !== "group") continue;
      if (v.match.home !== team && v.match.away !== team) continue;
      if (v.status !== "scheduled") continue;
      next.push({
        id: v.match.id,
        label: `${t("thirds.col.group")} ${v.match.group ?? "?"} · ${t("stats.col.played.abbr")}${v.match.matchday ?? "?"}`,
        opp: v.match.home === team ? v.match.away : v.match.home,
      });
    }
    // prochain(s) match(s) KO : nœud du tableau où l'équipe est placée, non joué
    for (const [nid, node] of Object.entries(state.bracket)) {
      if (node.a !== team && node.b !== team) continue;
      const id = Number(nid.slice(1));
      const view = state.byId.get(id);
      if (view?.status === "finished" && view.displayScore) continue;
      next.push({ id, label: roundShort(id), opp: node.a === team ? node.b : node.a });
    }
    next.sort((a, b) => a.id - b.id);
  }
  return { played, next, status };
}

function statusBadge(s: Status): string {
  if (s === "champion") return `<span class="tt-badge champ">🏆 ${t("common.champion")}</span>`;
  if (s === "eliminated") return `<span class="tt-badge out">${t("common.eliminated")}</span>`;
  return "";
}

function journeyHtml(team: string, j: Journey): string {
  const row = (l: Result): string =>
    `<div class="bk-tip-row ${l.outcome}"><span class="bt-r">${esc(l.label)}</span><span class="bt-s">${esc(l.scoreStr)}</span><span class="bt-o">${flagSpan(l.opp)}</span></div>`;
  const nextRow = (n: Upcoming): string =>
    `<div class="bk-tip-row up"><span class="bt-r"><span class="ar">→</span> ${esc(n.label)}</span><span class="bt-s"></span><span class="bt-o">${n.opp ? flagSpan(n.opp) : `<span class="dim">?</span>`}</span></div>`;
  const sep = j.next.length ? `<div class="tt-sep">${t("common.upcoming")}</div>` : "";
  return `<div class="bk-tip-h">${flagSpan(team, { name: true })}${statusBadge(j.status)}</div>${j.played.map(row).join("")}${sep}${j.next.map(nextRow).join("")}`;
}

// --- contrôleur global -----------------------------------------------------

interface Provider {
  root: HTMLElement;
  get: () => OfficialState | null;
}
const providers: Provider[] = [];
let tip: HTMLElement | null = null;
let pinned: HTMLElement | null = null;
let installed = false;

function ensureTip(): HTMLElement {
  if (!tip) {
    tip = document.createElement("div");
    tip.className = "bk-tip hide";
    document.body.appendChild(tip);
  }
  return tip;
}

function stateFor(flag: HTMLElement): OfficialState | null {
  for (const p of providers) {
    if (p.root.isConnected && p.root.contains(flag)) {
      const s = p.get();
      if (s) return s;
    }
  }
  return null;
}

function place(flag: HTMLElement, t: HTMLElement): void {
  const r = flag.getBoundingClientRect();
  t.style.left = "0px";
  t.style.top = "0px";
  const tw = t.offsetWidth;
  const th = t.offsetHeight;
  const left = Math.max(8, Math.min(r.left, window.innerWidth - tw - 8));
  let top = r.bottom + 8;
  if (top + th > window.innerHeight - 8) top = r.top - th - 8;
  t.style.left = `${left}px`;
  t.style.top = `${Math.max(8, top)}px`;
}

function showFor(flag: HTMLElement): void {
  const team = flag.dataset.team;
  if (!team) return;
  const state = stateFor(flag);
  if (!state) return;
  const j = buildJourney(state, team);
  if (j.played.length === 0 && j.next.length === 0) return;
  const t = ensureTip();
  t.innerHTML = journeyHtml(team, j);
  t.classList.remove("hide");
  place(flag, t);
}

function flagFrom(e: Event): HTMLElement | null {
  const target = e.target;
  if (!(target instanceof Element)) return null;
  // Les drapeaux DANS une carte de match sont gérés ici aussi (parcours de
  // l'équipe) ; le panneau « plateau TV » s'efface alors, voir matchPanel.ts.
  return target.closest<HTMLElement>(".f[data-team]");
}

/** Enregistre une vue (conteneur + accès à son état) et installe l'écouteur. */
export function registerTeamTooltips(root: HTMLElement, get: () => OfficialState | null): void {
  providers.push({ root, get });
  if (installed) return;
  installed = true;
  document.addEventListener("mouseover", (e) => {
    if (pinned) return;
    const f = flagFrom(e);
    if (f) showFor(f);
  });
  document.addEventListener("mouseout", (e) => {
    if (flagFrom(e) && !pinned) tip?.classList.add("hide");
  });
  // Le CLIC/TAP sur un drapeau ouvre la FICHE PAYS (countryProfile) ; ici on se
  // contente de refermer l'aperçu de survol pour ne pas le laisser par-dessus.
  document.addEventListener("click", () => {
    pinned = null;
    tip?.classList.add("hide");
  });
}
